import React, { useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  BarChart2,
  LogOut,
  Plus,
  ChevronRight,
  Pencil,
  Shield,
  Cpu,
  Map,
  Sparkles,
  PanelLeftClose,
  PanelRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { cn } from '../lib/utils';
import { useDispatch } from 'react-redux';
import { signOutSaga } from '../redux/actions/authActions';
import { clearTokens } from '../services/auth';
import crcEvansLogo from '../assets/crc-evans-logo.png';
import crcEvansLogoArchive from '../assets/crc-evans-logo-archive.png';
import { useUserPermissionsAfterLogin } from '../features/admin/hooks/useUserPermissionsAfterLogin';
import { BACKEND_VERSION } from '../version';
import { BUILD_VERSION, GIT_COMMIT_SHORT } from '../buildInfo.generated';
import { NEW_FEATURES, ENHANCEMENTS, BUG_FIXES } from '../constants/releaseNotes';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  onCreateClick?: () => void;
  onEditClick?: () => void;
  showBackNavigation?: boolean;
  mode?: 'create' | 'edit' | 'default';
  onBack?: () => void;
  setShowBackNavigation?: (showBackNavigation: boolean) => void;
}

const SIDEBAR_COMPACT_KEY = 'data360.sidebarCompact';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  key?: string;
  /** When true, NavLink uses `end` so only exact path matches (e.g. /Map). */
  end?: boolean;
}

const navLinkClass = (isActive: boolean, sidebarCompact: boolean) =>
  [
    'flex items-center gap-3 rounded-lg text-[13px] font-medium transition-all duration-150 py-2.5',
    sidebarCompact
      ? 'px-0 justify-center lg:justify-center'
      : 'px-0 justify-center lg:px-3 lg:justify-start',
    isActive
      ? 'bg-hero-red/15 text-white'
      : 'text-white/50 hover:text-white/80 hover:bg-white/5',
  ].join(' ');

const Layout: React.FC<LayoutProps> = ({
  children,
  title = 'Projects',
  subtitle,
  onCreateClick,
  onEditClick,
  showBackNavigation = false,
  onBack,
  setShowBackNavigation,
}) => {
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = React.useState(false);
  const [sidebarCompact, setSidebarCompact] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const backendVersion = BACKEND_VERSION;

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { hasPermission } = useUserPermissionsAfterLogin();

  const firstName: string = user?.['custom:first_name'] || '';
  const lastName: string = user?.['custom:last_name'] || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() ||
    (firstName.slice(0, 2) || 'U').toUpperCase();
  const roleLabel: string = user?.['custom:role'] || user?.role || '';

  const navItems: NavItem[] = [
    {
      label: 'Projects',
      icon: <Home className="h-[18px] w-[18px]" />,
      href: '/Projects',
      key: 'view_projects',
    },
    {
      label: 'Analysis',
      icon: <BarChart2 className="h-[18px] w-[18px]" />,
      href: '/Analysis',
      key: 'view_analytics',
    },
    {
      label: 'Map',
      icon: <Map className="h-[18px] w-[18px]" />,
      href: '/Map',
      key: 'view_map',
      end: true,
    },
    {
      label: 'Devices',
      icon: <Cpu className="h-[18px] w-[18px]" />,
      href: '/Devices',
      key: 'view_devices',
      end: true,
    },
    {
      label: 'Admin',
      icon: <Shield className="h-[18px] w-[18px]" />,
      href: '/Admin',
      key: 'manage_admins',
      end: true,
    },
  ];

  useEffect(() => {
    try {
      setSidebarCompact(localStorage.getItem(SIDEBAR_COMPACT_KEY) === '1');
    } catch {
      /* ignore */
    }
  }, []);

  const toggleSidebarCompact = () => {
    setSidebarCompact((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COMPACT_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    return () => {
      setIsLoggingOut(false);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === '/login') {
      setIsLoggingOut(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    if (isLoggingOut) return;
    try {
      setIsLoggingOut(true);
      clearTokens();
      dispatch(signOutSaga());
      setTimeout(() => {
        window.location.href = '/login';
      }, 10000);
    } catch {
      setIsLoggingOut(false);
    }
  };

  const pageTitle =
    location.pathname.startsWith('/Projects')
      ? 'Projects'
      : location.pathname === '/Devices'
        ? 'Devices'
        : location.pathname === '/Analysis'
          ? 'Analysis'
          : location.pathname === '/Map'
            ? 'Map'
            : location.pathname === '/Admin'
              ? 'Admin'
              : title;

  const renderReleaseSection = (heading: string, items: string[]) => (
    <div className="space-y-3">
      <h4 className="font-heading text-[14px] font-semibold text-blueprint-blue">{heading}</h4>
      <div className="border-l-[3px] border-hero-red pl-4 py-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            {items.map((item, index) => (
              <p key={index} className="text-[13px] text-text-grey">
                {item}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderReleaseNotesContent = () => (
    <div className="space-y-4">
      {renderReleaseSection('New Features', NEW_FEATURES)}
      {renderReleaseSection('Enhancements', ENHANCEMENTS)}
      {renderReleaseSection('Bug Fixes', BUG_FIXES)}
    </div>
  );

  return (
    <div className="min-h-screen h-screen flex bg-page-bg overflow-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 bottom-0 z-50 flex w-14 flex-col overflow-hidden bg-blueprint-blue transition-[width] duration-200 ease-out',
          sidebarCompact ? 'lg:w-14' : 'lg:w-60'
        )}
      >
        {/* Brand header */}
        <div className="flex h-[60px] flex-shrink-0 items-center justify-center gap-2 border-b border-white/10 px-2 lg:px-3">
          <div
            className={cn(
              'flex min-w-0 flex-1 items-center gap-3',
              sidebarCompact ? 'justify-center' : 'justify-center lg:justify-start'
            )}
          >
            <img
              src={crcEvansLogo}
              alt="CRC Evans"
              className="h-8 w-8 flex-shrink-0 object-contain"
            />
            <div className={cn('min-w-0', !sidebarCompact ? 'hidden lg:block' : 'hidden')}>
              <div className="font-heading text-[14px] font-semibold leading-tight text-white">
                DATA 360
              </div>
              <div className="text-[11px] text-white/40">CRC Evans Platform</div>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleSidebarCompact}
            className="hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/5 hover:text-white lg:inline-flex"
            aria-label={sidebarCompact ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCompact ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCompact ? (
              <PanelRight className="h-[18px] w-[18px] mr-2" />
            ) : (
              <PanelLeftClose className="h-[18px] w-[18px]" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav
          className={cn(
            'flex flex-1 flex-col gap-1 overflow-y-auto py-3',
            sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3'
          )}
        >
          {navItems
            .filter((item) => hasPermission(item.key || ''))
            .map((item) => (
              <NavLink
                key={item.label}
                to={item.href}
                end={item.end}
                title={item.label}
                className={({ isActive }) => navLinkClass(isActive, sidebarCompact)}
                onClick={() => {
                  if (item.href === '/Projects' && onBack) {
                    onBack();
                  }
                  setShowBackNavigation?.(false);
                }}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                <span className={cn(!sidebarCompact ? 'hidden lg:inline' : 'hidden')}>{item.label}</span>
              </NavLink>
            ))}
        </nav>

        {/* Footer stack: user → What's New → Logout → version */}
        <div className="flex-shrink-0 border-t border-white/10">
          {/* User profile */}
          <div className={cn('py-3', sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3')}>
            <div
              className={cn(
                'flex items-center gap-3 rounded-lg py-2',
                sidebarCompact ? 'justify-center px-0 lg:px-0' : 'justify-center px-0 lg:justify-start lg:px-2'
              )}
              title={fullName || 'User'}
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-hero-red">
                <span className="font-heading text-[12px] font-semibold text-white">{initials}</span>
              </div>
              <div className={cn('min-w-0 flex-1', !sidebarCompact ? 'hidden lg:block' : 'hidden')}>
                <div className="truncate text-[13px] font-medium text-white">{fullName || 'User'}</div>
                {roleLabel && (
                  <div className="truncate text-[11px] text-white/40">{roleLabel}</div>
                )}
              </div>
            </div>
          </div>

          {/* What's New */}
          <div className={cn(sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3')}>
            <button
              type="button"
              className={cn(
                'mb-2 flex w-full items-center gap-3 rounded-lg border border-white/15 text-[13px] font-medium text-white/75 transition-all duration-150 hover:bg-white/5 hover:text-white',
                sidebarCompact
                  ? 'justify-center px-0 py-2 lg:justify-center lg:px-0'
                  : 'justify-center px-0 py-2 lg:justify-start lg:px-3'
              )}
              onClick={() => setIsReleaseNotesOpen(true)}
              title="What's New"
            >
              <Sparkles className="h-4 w-4 flex-shrink-0" />
              <span className={cn(!sidebarCompact ? 'hidden lg:inline' : 'hidden')}>What&apos;s New</span>
            </button>
          </div>

          {/* Logout */}
          <div className={cn('pb-2 pt-1', sidebarCompact ? 'px-2 lg:px-2' : 'px-2 lg:px-3')}>
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-3 rounded-lg text-[13px] font-medium text-hero-red-30 transition-all duration-150 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50',
                sidebarCompact
                  ? 'justify-center px-0 py-2 lg:justify-center lg:px-0'
                  : 'justify-center px-0 py-2 lg:justify-start lg:px-3'
              )}
              onClick={handleLogout}
              disabled={isLoggingOut}
              title={isLoggingOut ? 'Signing out…' : 'Logout'}
            >
              {isLoggingOut ? (
                <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-hero-red-30 border-t-transparent" />
              ) : (
                <LogOut className="h-4 w-4 flex-shrink-0" />
              )}
              <span className={cn(!sidebarCompact ? 'hidden lg:inline' : 'hidden')}>
                {isLoggingOut ? 'Signing out…' : 'Logout'}
              </span>
            </button>
          </div>

          {/* Version + Powered by */}
          <div
            className={cn(
              'border-t border-white/10 px-4 py-3',
              !sidebarCompact ? 'hidden lg:block' : 'hidden'
            )}
          >
            <div className="mb-5 text-center text-[10px] text-white/25">
              Build {BUILD_VERSION} ({GIT_COMMIT_SHORT})
            </div>
            <div className="mb-1 text-center text-[10px] text-white/25">Powered by</div>
            <a
              href="https://www.crcevans.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-3 flex items-center justify-center opacity-80 transition-opacity hover:opacity-100"
            >
              <img src={crcEvansLogoArchive} alt="CRC Evans" className="h-7 w-auto object-contain" />
            </a>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div
        className={cn(
          'ml-14 flex min-h-0 min-w-0 flex-1 flex-col transition-[margin] duration-200 ease-out',
          sidebarCompact ? 'lg:ml-14' : 'lg:ml-60'
        )}
      >
        {/* Header */}
        <header className="h-[60px] bg-white/80 backdrop-blur-xl border-b border-[#E2E5EA] flex-shrink-0 sticky top-0 z-40">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between gap-4">
            <div className="flex flex-col min-w-0">
              {showBackNavigation ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (onBack) {
                        onBack();
                        navigate('/Projects');
                      }
                    }}
                    className="font-heading text-[18px] font-semibold text-blueprint-blue hover:text-hero-red transition-colors"
                    title="Back to projects"
                  >
                    Projects
                  </button>
                  <ChevronRight className="h-4 w-4 text-heritage-grey flex-shrink-0 mt-1" />
                  <span className="font-heading text-[18px] font-semibold text-blueprint-blue truncate">
                    {subtitle}
                  </span>
                </div>
              ) : (
                <>
                  <h1 className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
                    {pageTitle}
                  </h1>
                  {subtitle && (
                    <p className="text-[11px] text-heritage-grey truncate">{subtitle}</p>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {!showBackNavigation && onCreateClick && hasPermission('create_projects') && (
                <button
                  type="button"
                  onClick={onCreateClick}
                  className="flex items-center gap-2 bg-hero-red text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-hero-red-60 transition-all duration-150"
                >
                  <Plus className="h-4 w-4" />
                  Create Project
                </button>
              )}
              {showBackNavigation && onEditClick && hasPermission('edit_projects') && (
                <button
                  type="button"
                  onClick={onEditClick}
                  className="flex items-center gap-2 bg-hero-red text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-hero-red-60 transition-all duration-150"
                >
                  <Pencil className="h-4 w-4" />
                  Edit Project
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 min-h-[calc(100vh-60px)] min-w-0 overflow-y-auto overflow-x-auto px-4 flex flex-col">
          <div className="pt-4 pb-0 flex-1 flex flex-col min-h-0 min-w-0 w-full">{children}</div>
        </main>
      </div>

      {/* What's New modal */}
      <Dialog open={isReleaseNotesOpen} onOpenChange={setIsReleaseNotesOpen}>
        <DialogContent className="sm:max-w-4xl bg-white max-h-[100vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[20px] font-semibold text-blueprint-blue flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-hero-red" />
              What's New
            </DialogTitle>
            <DialogDescription className="text-[13px] text-text-grey">
              Latest updates and features in{' '}
              <span className="font-semibold">
                <span className="text-hero-red">DATA</span>{' '}
                <span className="text-heritage-grey">360</span>
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-6">
            <div className="bg-page-bg rounded-lg p-4 border border-[#E2E5EA]">
              <h3 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-[0.06em] mb-2">
                Current Versions
              </h3>
              <div className="space-y-1 text-[13px] text-text-grey">
                <p>
                  Frontend:{' '}
                  <span className="font-mono font-semibold text-blueprint-blue">
                    v{BUILD_VERSION} ({GIT_COMMIT_SHORT})
                  </span>
                </p>
                <p>
                  Backend:{' '}
                  <span className="font-mono font-semibold text-blueprint-blue">
                    v{backendVersion}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 bg-page-bg/50">
            <div className="space-y-4">
              <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">
                Recent Updates
              </h3>
              <div className="relative h-[600px] overflow-hidden">
                <div className="space-y-4">{renderReleaseNotesContent()}</div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
